import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

interface RestaurantSession {
  userId: string;
  token?: string;
  username: string;
  restaurantId: string;
  role: string;
}

interface RestaurantAuthContextType {
  isAuthenticated: boolean;
  authReady: boolean;
  session: RestaurantSession | null;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const RestaurantAuthContext = createContext<RestaurantAuthContextType | null>(null);

export function RestaurantAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<RestaurantSession | null>(() => {
    const stored = sessionStorage.getItem("kf-restaurant-session");
    return stored ? JSON.parse(stored) : null;
  });
  // authReady = true once we've confirmed there's a live supabase.auth session
  // (or there's no custom session token to bridge from).
  const [authReady, setAuthReady] = useState(false);

  const isAuthenticated = !!session;

  // Re-bridge when needed: ensure supabase.auth holds a session whose user.id
  // matches the current restaurant session userId, so RLS evaluates correctly.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sess = session;
      if (!sess) { setAuthReady(true); return; }

      // (a) Legacy session without a token cannot be rebridged — force logout.
      if (!sess.token) {
        console.info("rebridge: no token → forcing logout");
        try { await supabase.auth.signOut(); } catch {}
        sessionStorage.removeItem("kf-restaurant-session");
        if (!cancelled) {
          setSession(null);
          setAuthReady(true);
        }
        return;
      }

      const { data: authSession } = await supabase.auth.getSession();
      const liveUserId = authSession?.session?.user?.id;

      if (liveUserId && liveUserId === sess.userId) {
        console.info("rebridge: existing session matches → ok");
        if (!cancelled) setAuthReady(true);
        return;
      }

      if (liveUserId && liveUserId !== sess.userId) {
        console.info("rebridge: session mismatch → rebridging");
      } else {
        console.info("rebridge: no session → rebridging");
      }

      try {
        const { data } = await supabase.functions.invoke("restaurant-rebridge", {
          body: { restaurant_session_id: sess.token },
        });
        if (cancelled) return;
        if (data?.authBridge?.token_hash) {
          const { error: otpErr } = await supabase.auth.verifyOtp({
            token_hash: data.authBridge.token_hash,
            type: "magiclink",
          });
          if (otpErr) {
            console.error("rebridge: failed", otpErr);
          } else {
            console.info("rebridge: success");
          }
        } else {
          console.error("rebridge: failed", data);
        }
      } catch (e) {
        console.error("rebridge: failed", e);
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.userId]);


  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke("restaurant-login", {
        body: { username, password },
      });

      if (error) return { success: false, error: "An error occurred. Please try again." };
      if (!data?.success) return { success: false, error: data?.error || "Invalid username or password" };

      const sess: RestaurantSession = data.session;

      // Establish a real Supabase Auth session via the bridged magic-link token.
      // RLS on menu/editor tables depends on auth.uid() being set.
      if (data.authBridge?.token_hash) {
        const { error: otpErr } = await supabase.auth.verifyOtp({
          token_hash: data.authBridge.token_hash,
          type: "magiclink",
        });
        if (otpErr) console.error("auth bridge verifyOtp failed", otpErr);
      }

      setSession(sess);
      sessionStorage.setItem("kf-restaurant-session", JSON.stringify(sess));
      return { success: true };
    } catch {
      return { success: false, error: "An error occurred. Please try again." };
    }
  };

  const logout = () => {
    const token = session?.token;
    if (token) {
      supabase.functions.invoke("session-logout", { body: { restaurant_session_id: token } }).catch(() => {});
    }
    supabase.auth.signOut().catch(() => {});
    setSession(null);
    sessionStorage.removeItem("kf-restaurant-session");
  };

  return (
    <RestaurantAuthContext.Provider value={{ isAuthenticated, authReady, session, login, logout }}>
      {children}
    </RestaurantAuthContext.Provider>
  );
}

export const useRestaurantAuth = () => {
  const ctx = useContext(RestaurantAuthContext);
  if (!ctx) throw new Error("useRestaurantAuth must be used within RestaurantAuthProvider");
  return ctx;
};
